import { reviewReduxConstants } from '@/constants';
import { Review, ReviewDetails, ReviewsSearchFilter, ReviewsSearchResult } from '@/types/reviews';

export type ReviewsState = {
  all: Record<string, Review>;
  byUid: Record<
    string,
    {
      ids: Array<string>;
      olderIds: Array<string>;
      newerIds: Array<string>;
      details: ReviewDetails;
      searchFilter?: ReviewsSearchFilter;
      searchResult?: ReviewsSearchResult;
    }
  >;
};

type ReviewsAction = {
  type: string;
  payload?: {
    uid: string;
    ids: Array<string>;
    all?: Record<string, Review>;
    concat?: boolean;
    reviewDetails: ReviewDetails;
    olderIds: Array<string>;
    newerIds: Array<string>;
    searchFilter: ReviewsSearchFilter;
    searchResult: ReviewsSearchResult;
    payload?: any;
  };
};

export function reviews(state = { all: {}, byUid: {} }, action: ReviewsAction): ReviewsState {
  switch (action.type) {
    case reviewReduxConstants.SET_REVIEW_LIST_ITEMS: {
      const { uid, ids, olderIds, newerIds, all, concat } = action.payload || {};

      if (!uid) {
        return state as ReviewsState;
      }

      if (!state.byUid[uid]) {
        return {
          ...state,
          all: { ...state.all, ...all },
          byUid: {
            ...state.byUid,
            [uid]: { ids: [...ids], olderIds: [...olderIds], newerIds: [...newerIds], details: {} as ReviewDetails },
          },
        };
      }

      return {
        ...state,
        all: { ...state.all, ...all },
        byUid: {
          ...state.byUid,
          [uid]: {
            ...state.byUid[uid],
            ids: concat ? [...state.byUid[uid].ids, ...ids] : [...ids],
            olderIds: concat ? [...(state.byUid[uid].olderIds || []), ...olderIds] : [...olderIds],
            newerIds: concat ? [...(state.byUid[uid].newerIds || []), ...newerIds] : [...newerIds],
          },
        },
      };
    }
    case reviewReduxConstants.SET_REVIEW_DETAILS: {
      const { uid, reviewDetails } = action.payload || {};

      if (!uid) {
        return state;
      }

      if (!state.byUid[uid]) {
        return {
          ...state,
          byUid: { ...state.byUid, [uid]: { ids: [], olderIds: [], newerIds: [], details: { ...reviewDetails } } },
        };
      }

      return {
        ...state,
        byUid: { ...state.byUid, [uid]: { ...state.byUid[uid], details: { ...reviewDetails } } },
      };
    }

    case reviewReduxConstants.RESET_REVIEWS_STATE: {
      const { uid } = action.payload || {};

      if (!uid) {
        return state;
      }

      return {
        ...state,
        byUid: {
          ...state.byUid,
          [uid]: {
            ...state.byUid[uid],
            searchFilter: {},
            searchResult: {
              loading: false,
              fetched: false,
              error: false,
              count: 0,
            },
            ids: [],
            olderIds: [],
            newerIds: [],
            details: {},
          },
        },
      };
    }

    case reviewReduxConstants.SET_REVIEWS_SEARCH_RESULT: {
      const { uid, searchResult } = action.payload || {};

      if (!uid) {
        return state;
      }

      if (!state.byUid[uid]) {
        return {
          ...state,
          byUid: {
            ...state.byUid,
            [uid]: {
              ids: [],
              details: {},
              searchResult: { ...searchResult },
            },
          },
        };
      }

      const currentState = state.byUid[uid];

      return {
        ...state,
        byUid: {
          ...state.byUid,
          [uid]: { ...currentState, searchResult: { ...currentState.searchResult, ...searchResult } },
        },
      };
    }
    default:
      return state;
  }
}
